/**
 * Per-file upload/processing status for a pending message.
 *
 * Replaces a single spinner for the whole batch: every file gets a row, the
 * transfer phase gets a real determinate bar (the backend upload reports request
 * body progress), and the label names the stage rather than saying "Processing...".
 * Elapsed time appears once the wait stops feeling instant.
**/

import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';

import { UploadFileProgress, UploadProgress as UploadProgressState } from '@/hooks/useFileUpload';
import { getFileIcon } from '@/utils/fileValidation';
import LoadingState from '@/components/Answer/LoadingState';

/** Long enough that a fast upload never shows a timer, short enough to reassure. */
const SHOW_ELAPSED_AFTER_MS = 10000;
const SHOW_SLOW_HINT_AFTER_MS = 30000;

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const STAGE_LABELS: Record<UploadFileProgress['stage'], string> = {
  queued: 'Queued',
  uploading: 'Uploading',
  uploaded: 'Uploaded',
  processing: 'Processing',
};

/** The headline: what is happening, to what, right now. */
const buildLabel = (progress: UploadProgressState): string => {
  const { files, phase, sentBytes, totalBytes } = progress;
  const subject = files.length === 1 ? files[0].name : `${files.length} files`;

  if (phase === 'uploading') {
    const percent = totalBytes > 0 ? Math.round((sentBytes / totalBytes) * 100) : 0;
    return `Uploading ${subject} — ${percent}% of ${formatBytes(totalBytes)}`;
  }
  if (phase === 'extracting') return `Extracting text from ${subject}`;
  if (phase === 'indexing') return `Indexing ${subject}`;
  return `Processing ${subject}`;
};

const FileRow = ({ file }: { file: UploadFileProgress }) => (
  <li className="flex items-center gap-2 text-xs my-1">
    <img src={getFileIcon(file.name)} width="16" height="16" alt="" className="shrink-0" />
    <span className={`truncate ${file.stage === 'queued' ? `text-black/40 dark:text-white/40` : `dark:text-white/75`}`}>
      {file.name}
    </span>
    <span className="shrink-0 text-black/40 dark:text-white/40">{formatBytes(file.size)}</span>
    <span className="ms-auto flex shrink-0 items-center gap-1 text-black/50 dark:text-white/50">
      {file.stage === 'uploaded' ? <Check size={12} className="text-success" /> : null}
      {file.stage === 'uploading' || file.stage === 'processing' ? (
        <span className="loading loading-spinner loading-xs opacity-50"></span>
      ) : null}
      {STAGE_LABELS[file.stage]}
    </span>
  </li>
);

const UploadProgress = ({ progress }: { progress: UploadProgressState }) => {
  const [elapsedMs, setElapsedMs] = useState(0);

  // Mounted once per upload, so a locally-owned start time is the elapsed time.
  useEffect(() => {
    const startedAt = Date.now();
    const timer = window.setInterval(() => setElapsedMs(Date.now() - startedAt), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const percent = progress.totalBytes > 0 ? Math.round((progress.sentBytes / progress.totalBytes) * 100) : 0;

  return (
    <div role="status" className="my-3 w-full max-w-[95%] rounded-xl p-3">
      <div className="flex items-center gap-2">
        <LoadingState label={buildLabel(progress)} />
        {elapsedMs >= SHOW_ELAPSED_AFTER_MS ? (
          <span className="ms-auto shrink-0 text-xs tabular-nums text-black/40 dark:text-white/40">
            {Math.floor(elapsedMs / 1000)}s
          </span>
        ) : null}
      </div>

      {progress.phase === 'uploading' && progress.totalBytes > 0 ? (
        <progress className="progress progress-primary mt-2 w-full" value={percent} max={100}></progress>
      ) : null}

      {progress.files.length > 1 ? (
        <ul className="mt-2 flex flex-col gap-1.5">
          {progress.files.map((file, idx) => <FileRow key={`${file.name}-${idx}`} file={file} />)}
        </ul>
      ) : null}

      {elapsedMs >= SHOW_SLOW_HINT_AFTER_MS ? (
        <p className="mt-2 text-xs text-black/40 dark:text-white/40">
          Large or scanned documents can take a minute to process.
        </p>
      ) : null}
    </div>
  );
};

export default UploadProgress;
